/**
 * SandboxPanel — Painel Inteligente do Modo Agente (Event-Driven)
 *
 * Inspirado na abordagem da Manus AI:
 * Em vez de VNC/polling cego, o painel **reage aos eventos SSE do agente**:
 *   - Quando o agente executa shell → Terminal mostra comando + output ao vivo
 *   - Quando o agente navega no browser → Desktop busca screenshot automaticamente
 *   - Quando o agente escreve código → Code view mostra o arquivo
 *
 * O painel tem 3 views:
 *   - Activity: Timeline de todas as ações do agente no sandbox (view padrão)
 *   - Desktop: Screenshot sob demanda do desktop X11
 *   - Terminal: Terminal interativo para comandos manuais
 *
 * @module components/sandbox/SandboxPanel
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Monitor,
  Terminal as TerminalIcon,
  Maximize2,
  Minimize2,
  StopCircle,
  Loader2,
  Circle,
  Send,
  ExternalLink,
  Camera,
  Code2,
  Activity,
  Globe,
  FileText,
  CheckCircle2,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Play,
  Pause,
  Copy,
  Check,
  Image as ImageIcon,
  RefreshCw,
} from "lucide-react";
import type { SandboxView } from "../../hooks/useSandbox.js";
import type { AgentEvent } from "../../hooks/useAgentSSE.js";
import { trpc } from "../../lib/trpc.js";

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------
interface SandboxPanelProps {
  vncUrl: string | null;
  wsUrl: string | null;
  sandboxUrl: string | null;
  activeView: SandboxView;
  onViewChange: (view: SandboxView) => void;
  onStop: () => void;
  isLoading: boolean;
  sessionId?: string;
  agentEvents?: AgentEvent[];
  isAgentRunning?: boolean;
}

interface SandboxAction {
  id: string;
  type: "shell" | "browser" | "file_read" | "file_write" | "code" | "status";
  tool: string;
  args: Record<string, unknown>;
  status: "running" | "success" | "error";
  command?: string;
  output?: string;
  error?: string;
  screenshotUrl?: string;
  filePath?: string;
  fileContent?: string;
  timestamp: number;
  duration?: number;
}

// ---------------------------------------------------------------------------
// Helper: Detectar se um tool name pertence ao sandbox
// ---------------------------------------------------------------------------
function isSandboxTool(ev: AgentEvent): boolean {
  // Opção 1: connector field (ReActAgent envia)
  if (ev.connector === "sandbox") return true;
  // Opção 2: fallback — detectar pelo nome da tool
  const tool = ev.tool || "";
  return (
    tool.startsWith("sandbox.") ||
    tool.includes("shell_exec") ||
    tool.includes("browser_navigate") ||
    tool.includes("browser_screenshot") ||
    tool.includes("file_read") ||
    tool.includes("file_write") ||
    tool.includes("code_run") ||
    tool.includes("sandbox_status")
  );
}

// ---------------------------------------------------------------------------
// Helper: Construir URL de screenshot autenticada
// ---------------------------------------------------------------------------
function buildScreenshotUrl(sessionId?: string): string {
  const apiBase = (import.meta as any).env?.VITE_API_URL || window.location.origin;
  const token = localStorage.getItem("accessToken");
  const params = new URLSearchParams({ t: String(Date.now()) });
  if (sessionId) params.set("sessionId", sessionId);
  if (token) params.set("token", token);
  return `${apiBase}/sandbox/screenshot?${params.toString()}`;
}

// ---------------------------------------------------------------------------
// Helper: Extrair ações do sandbox dos eventos SSE
// ---------------------------------------------------------------------------
function extractSandboxActions(events: AgentEvent[]): SandboxAction[] {
  const actions: SandboxAction[] = [];

  for (const ev of events) {
    if (!isSandboxTool(ev)) continue;

    if (ev.type === "tool_start") {
      const key = `${ev.step}-${ev.tool}`;

      const action: SandboxAction = {
        id: key,
        type: getActionType(ev.tool || ""),
        tool: ev.tool || "unknown",
        args: ev.args || {},
        status: "running",
        timestamp: ev.timestamp,
      };

      // Extrair info legível dos args
      if (ev.tool?.includes("shell_exec")) {
        action.command = (ev.args?.command as string) || "";
      } else if (ev.tool?.includes("browser_navigate")) {
        action.command = `Navegando para: ${(ev.args?.url as string) || ""}`;
      } else if (ev.tool?.includes("browser_screenshot")) {
        action.command = "Capturando screenshot do desktop";
      } else if (ev.tool?.includes("file_write")) {
        action.filePath = (ev.args?.path as string) || "";
        action.fileContent = (ev.args?.content as string) || "";
        action.command = `Escrevendo: ${action.filePath}`;
      } else if (ev.tool?.includes("file_read")) {
        action.filePath = (ev.args?.path as string) || "";
        action.command = `Lendo: ${action.filePath}`;
      } else if (ev.tool?.includes("code_run")) {
        action.command = `Executando ${(ev.args?.language as string) || "código"}`;
        action.fileContent = (ev.args?.code as string) || "";
      } else if (ev.tool?.includes("status")) {
        action.command = "Verificando status do sandbox";
      }

      actions.push(action);
    }

    if (ev.type === "tool_result") {
      // Encontrar a ação correspondente
      const key = `${ev.step}-${ev.tool}`;
      const existing = actions.find((a) => a.id === key);
      if (existing) {
        existing.status = ev.status === "success" ? "success" : "error";
        existing.output = ev.preview || "";
        existing.error = ev.error || undefined;
        existing.duration = ev.executionTime;
      }
    }
  }

  return actions;
}

function getActionType(tool: string): SandboxAction["type"] {
  if (tool.includes("shell_exec")) return "shell";
  if (tool.includes("browser")) return "browser";
  if (tool.includes("file_read")) return "file_read";
  if (tool.includes("file_write")) return "file_write";
  if (tool.includes("code_run")) return "code";
  if (tool.includes("status")) return "status";
  return "shell";
}

function getActionIcon(type: SandboxAction["type"]) {
  switch (type) {
    case "shell": return TerminalIcon;
    case "browser": return Globe;
    case "file_read": return FileText;
    case "file_write": return Code2;
    case "code": return Play;
    case "status": return Activity;
    default: return TerminalIcon;
  }
}

function getActionLabel(type: SandboxAction["type"]) {
  switch (type) {
    case "shell": return "Terminal";
    case "browser": return "Browser";
    case "file_read": return "Leitura";
    case "file_write": return "Escrita";
    case "code": return "Código";
    case "status": return "Status";
    default: return "Ação";
  }
}

// ---------------------------------------------------------------------------
// Componente: Screenshot Inline (para Activity View)
// Cada instância busca screenshot independentemente usando actionId como key
// ---------------------------------------------------------------------------
function InlineScreenshot({ sessionId, actionId }: { sessionId?: string; actionId: string }) {
  const [imgSrc, setImgSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const fetchScreenshot = async () => {
      try {
        setLoading(true);
        setError(false);
        const url = buildScreenshotUrl(sessionId);
        const token = localStorage.getItem("accessToken");
        const headers: Record<string, string> = {};
        if (token) headers["Authorization"] = `Bearer ${token}`;
        const response = await fetch(url, { headers });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const blob = await response.blob();
        if (!cancelled) {
          // Cleanup previous URL
          if (imgSrc) URL.revokeObjectURL(imgSrc);
          const objectUrl = URL.createObjectURL(blob);
          setImgSrc(objectUrl);
          setLoading(false);
        }
      } catch {
        if (!cancelled) {
          setError(true);
          setLoading(false);
        }
      }
    };
    // Delay 2s to let the page load after navigation
    const timer = setTimeout(fetchScreenshot, 2000);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [sessionId, actionId, retryCount]);

  // Cleanup object URL on unmount
  useEffect(() => {
    return () => {
      if (imgSrc) URL.revokeObjectURL(imgSrc);
    };
  }, [imgSrc]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-3 bg-[#0d0d1a] rounded-lg border border-border/30">
        <Loader2 className="w-4 h-4 animate-spin text-primary" />
        <span className="text-[11px] text-muted-foreground">Capturando screenshot...</span>
      </div>
    );
  }

  if (error || !imgSrc) {
    return (
      <div className="flex items-center justify-between p-2 bg-red-500/5 rounded-lg border border-red-500/20">
        <div className="flex items-center gap-2">
          <AlertCircle className="w-3 h-3 text-red-400" />
          <span className="text-[10px] text-red-400">Screenshot indisponível</span>
        </div>
        <button
          onClick={() => setRetryCount((c) => c + 1)}
          className="p-1 rounded hover:bg-red-500/10 text-red-400 transition-colors"
          title="Tentar novamente"
        >
          <RefreshCw className="w-3 h-3" />
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-lg overflow-hidden border border-border/30 bg-black relative group">
      <img
        src={imgSrc}
        alt="Screenshot do sandbox"
        className="w-full h-auto max-h-[300px] object-contain"
      />
      <button
        onClick={() => setRetryCount((c) => c + 1)}
        className="absolute top-1.5 right-1.5 p-1 rounded bg-black/50 hover:bg-black/70 text-white/50 hover:text-white/80 opacity-0 group-hover:opacity-100 transition-opacity"
        title="Atualizar screenshot"
      >
        <RefreshCw className="w-3 h-3" />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Componente: Activity View (Timeline de ações do agente — view principal)
// ---------------------------------------------------------------------------
function ActivityView({
  actions,
  isAgentRunning,
  sessionId,
}: {
  actions: SandboxAction[];
  isAgentRunning: boolean;
  sessionId?: string;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [expandedActions, setExpandedActions] = useState<Set<string>>(new Set());
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Auto-scroll para a última ação
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [actions.length]);

  // Auto-expandir a ação mais recente
  useEffect(() => {
    if (actions.length > 0) {
      const lastAction = actions[actions.length - 1];
      setExpandedActions((prev) => {
        const next = new Set(prev);
        next.add(lastAction.id);
        return next;
      });
    }
  }, [actions.length]);

  const toggleExpand = (id: string) => {
    setExpandedActions((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (actions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center px-6">
        <div className="w-16 h-16 rounded-2xl bg-primary/5 flex items-center justify-center mb-4">
          <Activity className="w-8 h-8 text-primary/40" />
        </div>
        <h3 className="text-sm font-semibold text-foreground mb-1">
          {isAgentRunning ? "Agente iniciando..." : "Sandbox Pronto"}
        </h3>
        <p className="text-xs text-muted-foreground max-w-[240px]">
          {isAgentRunning
            ? "As ações do agente aparecerão aqui em tempo real."
            : "Envie uma tarefa para o agente e acompanhe a execução ao vivo aqui."}
        </p>
        {isAgentRunning && (
          <div className="flex gap-1 mt-4">
            <span className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
            <span className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
            <span className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      ref={scrollRef}
      className="flex flex-col gap-2 h-full overflow-y-auto p-3 scrollbar-thin"
    >
      {/* Header com contagem */}
      <div className="flex items-center justify-between px-1 pb-1 border-b border-border/30">
        <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
          Atividade do Agente
        </span>
        <span className="text-[10px] text-muted-foreground">
          {actions.length} {actions.length === 1 ? "ação" : "ações"}
        </span>
      </div>

      {/* Timeline de ações */}
      {actions.map((action, idx) => {
        const Icon = getActionIcon(action.type);
        const isExpanded = expandedActions.has(action.id);
        const isLast = idx === actions.length - 1;
        const isBrowserAction = action.type === "browser";
        // Screenshots agora são exibidos apenas na aba Desktop (live view)

        return (
          <div key={action.id} className="relative">
            {/* Linha de conexão da timeline */}
            {idx < actions.length - 1 && (
              <div className="absolute left-[15px] top-[32px] bottom-0 w-px bg-border/40" />
            )}

            {/* Card da ação */}
            <button
              onClick={() => toggleExpand(action.id)}
              className={`w-full text-left flex items-start gap-3 p-2.5 rounded-lg transition-all ${
                isExpanded
                  ? "bg-secondary/60 border border-border/60"
                  : "hover:bg-secondary/30"
              } ${isLast && action.status === "running" ? "ring-1 ring-primary/30" : ""}`}
            >
              {/* Ícone com status */}
              <div
                className={`w-[30px] h-[30px] rounded-lg flex items-center justify-center shrink-0 ${
                  action.status === "running"
                    ? "bg-primary/10 text-primary"
                    : action.status === "success"
                    ? "bg-green-500/10 text-green-500"
                    : "bg-red-500/10 text-red-500"
                }`}
              >
                {action.status === "running" ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Icon className="w-4 h-4" />
                )}
              </div>

              {/* Conteúdo */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {getActionLabel(action.type)}
                  </span>
                  {action.status === "success" && (
                    <CheckCircle2 className="w-3 h-3 text-green-500" />
                  )}
                  {action.status === "error" && (
                    <AlertCircle className="w-3 h-3 text-red-500" />
                  )}
                  {action.duration && (
                    <span className="text-[9px] text-muted-foreground/60">
                      {(action.duration / 1000).toFixed(1)}s
                    </span>
                  )}
                  {isBrowserAction && action.status === "success" && (
                    <ImageIcon className="w-3 h-3 text-blue-400 ml-auto mr-1" />
                  )}
                  <div className={isBrowserAction && action.status === "success" ? "" : "ml-auto"}>
                    {isExpanded ? (
                      <ChevronUp className="w-3 h-3 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="w-3 h-3 text-muted-foreground" />
                    )}
                  </div>
                </div>

                {/* Comando/descrição (sempre visível) */}
                {action.command && (
                  <p className="text-xs font-mono text-foreground/80 mt-0.5 truncate">
                    {action.command.length > 80
                      ? action.command.slice(0, 80) + "..."
                      : action.command}
                  </p>
                )}
              </div>
            </button>

            {/* Conteúdo expandido */}
            {isExpanded && (
              <div className="ml-[42px] mt-1 space-y-2 pb-2">
                {/* Comando completo (se truncado) */}
                {action.command && action.command.length > 80 && (
                  <div className="relative group">
                    <pre className="text-[11px] font-mono bg-[#0d0d1a] text-cyan-300 p-2.5 rounded-lg overflow-x-auto whitespace-pre-wrap break-all border border-border/30">
                      <span className="text-cyan-500/60">$ </span>
                      {action.command}
                    </pre>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        copyToClipboard(action.command || "", `cmd-${action.id}`);
                      }}
                      className="absolute top-1.5 right-1.5 p-1 rounded bg-white/5 hover:bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      {copiedId === `cmd-${action.id}` ? (
                        <Check className="w-3 h-3 text-green-400" />
                      ) : (
                        <Copy className="w-3 h-3 text-white/40" />
                      )}
                    </button>
                  </div>
                )}

                {/* Browser actions: mostrar URL navegada em vez de screenshot inline */}
                {isBrowserAction && action.status === "success" && isExpanded && action.output && (
                  <div className="flex items-center gap-2 p-2 bg-blue-500/5 rounded-lg border border-blue-500/20">
                    <Globe className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                    <span className="text-[11px] text-blue-300 font-mono truncate">{action.output}</span>
                  </div>
                )}

                {/* Output */}
                {action.output && !isBrowserAction && (
                  <div className="relative group">
                    <pre className="text-[11px] font-mono bg-[#0d0d1a] text-green-300/80 p-2.5 rounded-lg overflow-x-auto whitespace-pre-wrap break-all border border-border/30 max-h-[200px] overflow-y-auto scrollbar-thin">
                      {action.output}
                    </pre>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        copyToClipboard(action.output || "", `out-${action.id}`);
                      }}
                      className="absolute top-1.5 right-1.5 p-1 rounded bg-white/5 hover:bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      {copiedId === `out-${action.id}` ? (
                        <Check className="w-3 h-3 text-green-400" />
                      ) : (
                        <Copy className="w-3 h-3 text-white/40" />
                      )}
                    </button>
                  </div>
                )}

                {/* Código (file_write / code_run) */}
                {action.fileContent && (
                  <div className="relative group">
                    {action.filePath && (
                      <div className="text-[10px] font-mono text-muted-foreground/60 mb-1 flex items-center gap-1">
                        <FileText className="w-3 h-3" />
                        {action.filePath}
                      </div>
                    )}
                    <pre className="text-[11px] font-mono bg-[#0d0d1a] text-amber-200/80 p-2.5 rounded-lg overflow-x-auto whitespace-pre-wrap break-all border border-border/30 max-h-[200px] overflow-y-auto scrollbar-thin">
                      {action.fileContent.length > 1000
                        ? action.fileContent.slice(0, 1000) + "\n\n... (truncado)"
                        : action.fileContent}
                    </pre>
                  </div>
                )}

                {/* Erro */}
                {action.error && (
                  <div className="text-[11px] text-red-400 bg-red-500/5 border border-red-500/20 p-2 rounded-lg">
                    {action.error}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Running indicator */}
      {isAgentRunning && (
        <div className="flex items-center gap-2 px-2 py-3">
          <Loader2 className="w-3.5 h-3.5 text-primary animate-spin" />
          <span className="text-[11px] text-muted-foreground">
            Agente executando...
          </span>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Componente: Desktop Viewer (Screenshot sob demanda)
// ---------------------------------------------------------------------------
function DesktopViewer({
  sandboxUrl,
  sessionId,
  lastBrowserAction,
  isAgentRunning,
}: {
  sandboxUrl: string | null;
  sessionId?: string;
  lastBrowserAction?: SandboxAction;
  isAgentRunning?: boolean;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isAutoRefresh, setIsAutoRefresh] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const getScreenshotUrl = useCallback(() => {
    return buildScreenshotUrl(sessionId);
  }, [sessionId]);

  const fetchScreenshot = useCallback(async () => {
    const url = getScreenshotUrl();
    if (!url) return;

    try {
      const token = localStorage.getItem("accessToken");
      const headers: Record<string, string> = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const response = await fetch(url, { headers });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const blob = await response.blob();
      const img = new Image();
      img.onload = () => {
        const canvas = canvasRef.current;
        const container = containerRef.current;
        if (!canvas || !container) return;

        const containerWidth = container.clientWidth;
        const containerHeight = container.clientHeight;
        const scale = Math.min(
          containerWidth / img.width,
          containerHeight / img.height
        );

        canvas.width = img.width * scale;
        canvas.height = img.height * scale;

        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = "high";
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        }

        setIsConnected(true);
        setError(null);
        setLastUpdate(new Date());
        URL.revokeObjectURL(img.src);
      };
      img.onerror = () => URL.revokeObjectURL(img.src);
      img.src = URL.createObjectURL(blob);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(`Falha: ${msg}`);
    }
  }, [getScreenshotUrl]);

  // Buscar screenshot quando o agente faz ação de browser
  useEffect(() => {
    if (lastBrowserAction && lastBrowserAction.status !== "running") {
      // Aguardar 1.5s para a página carregar e buscar screenshot
      const timer = setTimeout(fetchScreenshot, 1500);
      return () => clearTimeout(timer);
    }
  }, [lastBrowserAction?.id, lastBrowserAction?.status, fetchScreenshot]);

  // Auto-enable auto-refresh quando agente está executando
  useEffect(() => {
    if (isAgentRunning && !isAutoRefresh) {
      setIsAutoRefresh(true);
    }
    // Desligar auto-refresh quando agente para (com delay para pegar último screenshot)
    if (!isAgentRunning && isAutoRefresh) {
      const timer = setTimeout(() => setIsAutoRefresh(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isAgentRunning]);

  // Auto-refresh toggle
  useEffect(() => {
    if (isAutoRefresh) {
      fetchScreenshot();
      intervalRef.current = setInterval(fetchScreenshot, 2000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isAutoRefresh, fetchScreenshot]);

  return (
    <div className="relative w-full h-full rounded-lg overflow-hidden border border-border bg-black">
      {/* Status bar */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-2 py-1 bg-black/70 backdrop-blur-sm z-10">
        <div className="flex items-center gap-2">
          <Circle
            className={`w-2 h-2 ${
              isConnected
                ? "text-green-400 fill-green-400"
                : "text-yellow-400 fill-yellow-400 animate-pulse"
            }`}
          />
          <span className="text-[10px] font-mono text-white/60">
            {isConnected ? "Desktop" : "Aguardando..."}
          </span>
          {lastUpdate && (
            <span className="text-[10px] font-mono text-white/40">
              {lastUpdate.toLocaleTimeString()}
            </span>
          )}
          {isAutoRefresh && (
            <span className="text-[9px] text-green-400/60 animate-pulse">LIVE</span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setIsAutoRefresh(!isAutoRefresh)}
            className={`p-0.5 rounded text-white/50 hover:text-white/80 transition-colors ${
              isAutoRefresh ? "bg-primary/30" : "hover:bg-white/10"
            }`}
            title={isAutoRefresh ? "Parar auto-refresh" : "Auto-refresh (2s)"}
          >
            {isAutoRefresh ? (
              <Pause className="w-3 h-3" />
            ) : (
              <Play className="w-3 h-3" />
            )}
          </button>
          <button
            onClick={fetchScreenshot}
            className="p-0.5 rounded hover:bg-white/10 text-white/50 hover:text-white/80 transition-colors"
            title="Capturar agora"
          >
            <Camera className="w-3 h-3" />
          </button>
        </div>
      </div>

      {error && (
        <div className="absolute bottom-2 left-2 right-2 z-10">
          <div className="bg-red-900/80 text-red-200 text-[10px] px-2 py-1 rounded font-mono">
            {error}
          </div>
        </div>
      )}

      <div
        ref={containerRef}
        className="w-full h-full flex items-center justify-center pt-6"
      >
        {!isConnected ? (
          <div className="text-center">
            <Monitor className="w-10 h-10 mx-auto mb-3 text-muted-foreground/30" />
            <p className="text-xs text-muted-foreground mb-2">
              Clique em capturar ou aguarde o agente navegar
            </p>
            <button
              onClick={fetchScreenshot}
              className="px-3 py-1.5 text-xs bg-primary/10 hover:bg-primary/20 text-primary rounded-lg transition-colors"
            >
              <Camera className="w-3 h-3 inline mr-1.5" />
              Capturar Desktop
            </button>
          </div>
        ) : (
          <canvas
            ref={canvasRef}
            className="max-w-full max-h-full object-contain"
            style={{ imageRendering: "auto" }}
          />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Componente: Terminal Interativo (via proxy megan-api)
// ---------------------------------------------------------------------------
function TerminalView({
  sessionId,
  sandboxUrl,
}: {
  sessionId?: string;
  sandboxUrl: string | null;
}) {
  const termRef = useRef<HTMLDivElement>(null);
  const [output, setOutput] = useState<string[]>([
    "\x1b[36m$ Sandbox terminal pronto.\x1b[0m",
    "  Use o terminal para executar comandos no ambiente Linux.",
    "",
  ]);
  const [currentCommand, setCurrentCommand] = useState("");
  const [isExecuting, setIsExecuting] = useState(false);
  const [commandHistory, setCommandHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);

  const executeMutation = trpc.sandbox.execute.useMutation();

  useEffect(() => {
    if (termRef.current) {
      termRef.current.scrollTop = termRef.current.scrollHeight;
    }
  }, [output]);

  const executeCommand = useCallback(
    async (cmd: string) => {
      if (!cmd.trim()) return;

      setIsExecuting(true);
      setOutput((prev) => [...prev, `$ ${cmd}`]);
      setCommandHistory((prev) => [...prev, cmd]);
      setHistoryIndex(-1);

      try {
        if (sessionId) {
          const result = await executeMutation.mutateAsync({
            sessionId,
            command: {
              type: "shell_exec",
              payload: { command: cmd, timeout: 30 },
            },
          });

          if (result.success && result.output) {
            setOutput((prev) => [...prev, ...(result.output ?? "").split("\n")]);
          } else if (result.error) {
            setOutput((prev) => [...prev, `Erro: ${result.error}`]);
          }
        } else if (sandboxUrl) {
          const response = await fetch(`${sandboxUrl}/v1/shell/exec`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ command: cmd, timeout: 30 }),
          });
          const data = await response.json();
          if (data.success && data.data?.output) {
            setOutput((prev) => [...prev, ...data.data.output.split("\n")]);
          } else if (data.error) {
            setOutput((prev) => [...prev, `Erro: ${data.error}`]);
          }
        } else {
          setOutput((prev) => [...prev, "Erro: Sandbox não conectado"]);
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setOutput((prev) => [...prev, `Erro: ${msg}`]);
      } finally {
        setIsExecuting(false);
        setTimeout(() => inputRef.current?.focus(), 50);
      }
    },
    [sessionId, sandboxUrl, executeMutation]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && !isExecuting) {
        const cmd = currentCommand.trim();
        if (cmd) {
          executeCommand(cmd);
          setCurrentCommand("");
        }
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        if (commandHistory.length > 0) {
          const newIndex =
            historyIndex === -1
              ? commandHistory.length - 1
              : Math.max(0, historyIndex - 1);
          setHistoryIndex(newIndex);
          setCurrentCommand(commandHistory[newIndex] || "");
        }
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        if (historyIndex >= 0) {
          const newIndex = historyIndex + 1;
          if (newIndex >= commandHistory.length) {
            setHistoryIndex(-1);
            setCurrentCommand("");
          } else {
            setHistoryIndex(newIndex);
            setCurrentCommand(commandHistory[newIndex] || "");
          }
        }
      } else if (e.key === "l" && e.ctrlKey) {
        e.preventDefault();
        setOutput([]);
      }
    },
    [currentCommand, isExecuting, executeCommand, commandHistory, historyIndex]
  );

  const renderLine = (line: string) => {
    const isPrompt = line.startsWith("$");
    const isError = line.startsWith("Erro:");
    return (
      <span
        className={
          isError
            ? "text-red-400"
            : isPrompt
            ? "text-cyan-400 font-semibold"
            : "text-green-300/80"
        }
      >
        {line}
      </span>
    );
  };

  return (
    <div className="flex flex-col h-full bg-[#0d0d1a] rounded-lg overflow-hidden border border-border">
      <div className="flex items-center gap-2 px-3 py-1.5 bg-[#16162a] border-b border-border/50">
        <Circle
          className={`w-2.5 h-2.5 ${
            sessionId || sandboxUrl
              ? "text-green-400 fill-green-400"
              : "text-red-400 fill-red-400"
          }`}
        />
        <span className="text-xs font-mono text-muted-foreground">
          {sessionId ? "Terminal (proxy seguro)" : sandboxUrl ? "Terminal (direto)" : "Desconectado"}
        </span>
        {isExecuting && (
          <Loader2 className="w-3 h-3 animate-spin text-primary ml-auto" />
        )}
      </div>

      <div
        ref={termRef}
        className="flex-1 overflow-y-auto p-3 font-mono text-xs leading-5"
        onClick={() => inputRef.current?.focus()}
      >
        {output.map((line, i) => (
          <div key={i} className="whitespace-pre-wrap break-all">
            {renderLine(line)}
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 px-3 py-2 bg-[#16162a] border-t border-border/50">
        <span className="text-cyan-400 font-mono text-xs font-bold">$</span>
        <input
          ref={inputRef}
          type="text"
          value={currentCommand}
          onChange={(e) => setCurrentCommand(e.target.value)}
          className="flex-1 bg-transparent text-green-300 font-mono text-xs outline-none placeholder:text-green-400/30"
          placeholder={isExecuting ? "Executando..." : "Digite um comando..."}
          onKeyDown={handleKeyDown}
          disabled={(!sessionId && !sandboxUrl) || isExecuting}
          autoFocus
        />
        <button
          onClick={() => {
            if (currentCommand.trim() && !isExecuting) {
              executeCommand(currentCommand.trim());
              setCurrentCommand("");
            }
          }}
          disabled={!currentCommand.trim() || isExecuting}
          className="p-1 rounded hover:bg-primary/20 text-muted-foreground hover:text-primary disabled:opacity-30 transition-colors"
        >
          <Send className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Componente Principal: SandboxPanel
// ---------------------------------------------------------------------------
export function SandboxPanel({
  vncUrl,
  wsUrl,
  sandboxUrl,
  activeView,
  onViewChange,
  onStop,
  isLoading,
  sessionId,
  agentEvents = [],
  isAgentRunning = false,
}: SandboxPanelProps) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // Extrair ações do sandbox dos eventos SSE
  const sandboxActions = useMemo(
    () => extractSandboxActions(agentEvents),
    [agentEvents]
  );

  // Última ação de browser (para trigger de screenshot automático)
  const lastBrowserAction = useMemo(
    () => [...sandboxActions].reverse().find((a) => a.type === "browser"),
    [sandboxActions]
  );

  // Auto-switch para Desktop quando o agente faz QUALQUER ação de browser com sucesso
  useEffect(() => {
    if (
      lastBrowserAction &&
      lastBrowserAction.status === "success" &&
      activeView !== "vnc"
    ) {
      // Auto-switch para Desktop tab quando agente interage com browser
      onViewChange("vnc" as SandboxView);
    }
  }, [lastBrowserAction?.id, lastBrowserAction?.status]);

  // Quando agente começa a rodar, iniciar na view Desktop (não Atividade)
  useEffect(() => {
    if (isAgentRunning && (activeView === "split" || activeView === "terminal")) {
      onViewChange("vnc" as SandboxView);
    }
  }, [isAgentRunning]);

  // Na montagem, se agente já está rodando, ir direto para Desktop
  useEffect(() => {
    if (isAgentRunning) {
      onViewChange("vnc" as SandboxView);
    }
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!panelRef.current) return;
    if (!isFullscreen) {
      panelRef.current.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
    setIsFullscreen(!isFullscreen);
  }, [isFullscreen]);

  useEffect(() => {
    const handler = () => {
      if (!document.fullscreenElement) setIsFullscreen(false);
    };
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  // View buttons — Activity é a view padrão (como a Manus)
  const viewButtons: {
    view: SandboxView;
    icon: typeof Monitor;
    label: string;
    badge?: number;
  }[] = [
    {
      view: "split" as SandboxView,
      icon: Activity,
      label: "Atividade",
      badge: sandboxActions.length || undefined,
    },
    { view: "vnc" as SandboxView, icon: Monitor, label: "Desktop" },
    { view: "terminal" as SandboxView, icon: TerminalIcon, label: "Terminal" },
  ];

  return (
    <div
      ref={panelRef}
      className={`flex flex-col h-full bg-card border border-border rounded-xl overflow-hidden ${
        isFullscreen ? "fixed inset-0 z-50" : ""
      }`}
    >
      {/* Toolbar */}
      <div className="flex items-center justify-between px-3 py-2 bg-card/80 backdrop-blur-sm border-b border-border shrink-0">
        {/* Esquerda: Título + Status */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <Circle className="w-2 h-2 text-green-400 fill-green-400 animate-pulse" />
            <span className="text-sm font-semibold text-foreground">
              Megan Sandbox
            </span>
          </div>
          {isAgentRunning && (
            <span className="text-[10px] text-primary bg-primary/10 px-1.5 py-0.5 rounded-full font-medium animate-pulse">
              Executando
            </span>
          )}
        </div>

        {/* Centro: View Switcher */}
        <div className="flex items-center gap-1 bg-secondary/50 rounded-lg p-0.5">
          {viewButtons.map(({ view, icon: Icon, label, badge }) => (
            <button
              key={view}
              onClick={() => onViewChange(view)}
              className={`flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-medium transition-all ${
                activeView === view
                  ? "bg-primary text-white shadow-sm"
                  : "text-muted-foreground hover:text-foreground hover:bg-secondary"
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
              {badge && badge > 0 && (
                <span
                  className={`text-[9px] px-1 py-0 rounded-full ${
                    activeView === view
                      ? "bg-white/20 text-white"
                      : "bg-primary/10 text-primary"
                  }`}
                >
                  {badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Direita: Ações */}
        <div className="flex items-center gap-1">
          {sandboxUrl && (
            <a
              href={sandboxUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1.5 rounded-lg hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
              title="Abrir sandbox em nova aba"
            >
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
          <button
            onClick={toggleFullscreen}
            className="p-1.5 rounded-lg hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
            title={isFullscreen ? "Sair do fullscreen" : "Fullscreen"}
          >
            {isFullscreen ? (
              <Minimize2 className="w-3.5 h-3.5" />
            ) : (
              <Maximize2 className="w-3.5 h-3.5" />
            )}
          </button>
          <button
            onClick={onStop}
            className="p-1.5 rounded-lg hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
            title="Encerrar sandbox"
          >
            <StopCircle className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">
                Provisionando sandbox...
              </p>
              <p className="text-xs text-muted-foreground/50 mt-1">
                Isso pode levar alguns segundos
              </p>
            </div>
          </div>
        ) : activeView === "split" ? (
          /* Activity View — Timeline de ações (view padrão, como a Manus) */
          <ActivityView
            actions={sandboxActions}
            isAgentRunning={isAgentRunning}
            sessionId={sessionId}
          />
        ) : activeView === "vnc" ? (
          /* Desktop ao vivo via screenshot sob demanda */
          <DesktopViewer
            sandboxUrl={sandboxUrl}
            sessionId={sessionId}
            lastBrowserAction={lastBrowserAction}
            isAgentRunning={isAgentRunning}
          />
        ) : (
          /* Terminal interativo */
          <TerminalView sessionId={sessionId} sandboxUrl={sandboxUrl} />
        )}
      </div>
    </div>
  );
}
